import { Expense } from '@easy-expense/data-schema-v2';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans } from '@easy-expense/ui-web-core';
import cuid from 'cuid';
import { Formik, useFormikContext } from 'formik';
import React from 'react';

import {
  SplitExpenseForm,
  SplitExpenseFormValues,
  SplitExpenseFormSchema,
} from './SplitExpenseForm.component';
import { Button } from '../../Button.components';
import { Modal } from '../../Shared/Modal.component';

export type { SplitExpenseFormValues as SplitExpenseForm };

const SplitExpenseModalFooter: React.FC<{ total: number }> = ({ total }) => {
  const { values, handleSubmit } = useFormikContext<SplitExpenseFormValues>();

  // Calculate canSave based on form values
  const canSave = React.useMemo(() => {
    if (!values.splits || values.splits.length < 2) {
      return false;
    }

    const allHaveTotals = values.splits.every(
      (split) => split.total !== undefined && split.total !== null && split.category,
    );
    if (!allHaveTotals) {
      return false;
    }

    const totalSum = values.splits.reduce((sum, split) => sum + (split.total ?? 0), 0);
    const totalError = Math.round((totalSum - total) * 100) / 100;

    return Math.abs(totalError) < 0.01;
  }, [values, total]);

  function onSubmit() {
    if (canSave) {
      handleSubmit();
    }
  }

  return (
    <Layout.Row
      style={{
        borderTop: `2px solid ${theme.colors.grayXLight}`,
      }}
      px={32}
      py={24}
      justify="flex-end"
    >
      <Button.Primary
        onClick={onSubmit}
        radius={50}
        style={{
          border: '2px solid #fff',
          boxShadow: '0 0 8px rgba(255, 255, 255, .5)',
        }}
        active={canSave}
      >
        <OpenSans.Custom size={15} weight="bold-700" style={{ color: theme.colors.buttonWhite }}>
          {getTranslation('SAVE')}
        </OpenSans.Custom>
        <Icon
          size={15}
          color={theme.colors.buttonWhite}
          style={{ paddingLeft: 10 }}
          name="chevron-forward"
        />
      </Button.Primary>
    </Layout.Row>
  );
};

export const SplitExpenseModal: React.FC<{
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  expense: Partial<Expense>;
  vendor: string;
  total: number;
  onSplit: (values: SplitExpenseFormValues) => void;
}> = ({ showModal, setShowModal, expense, vendor, total, onSplit }) => {
  const initialValues: SplitExpenseFormValues = React.useMemo(
    () => ({
      expense,
      splits: [
        {
          category: expense.category,
          total: expense.total ?? undefined,
          key: cuid(),
        },
        {
          category: expense.category,
          total: undefined,
          key: cuid(),
        },
      ],
    }),
    [expense],
  );

  function handleSubmit(values: SplitExpenseFormValues) {
    onSplit(values);
    setShowModal(false);
  }

  function validate(values: SplitExpenseFormValues) {
    const result = SplitExpenseFormSchema.safeParse(values);
    if (!result.success) {
      const errors: any = {};
      result.error.errors.forEach((error) => {
        const path = error.path.join('.');
        errors[path] = error.message;
      });
      return errors;
    }
    return {};
  }

  // Generate a new key when modal opens to force form reset
  const formKey = React.useMemo(() => {
    return showModal ? cuid() : '';
  }, [showModal]);

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      validate={validate}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      <Modal
        showModal={showModal}
        setShowModal={setShowModal}
        px={0}
        py={0}
        footer={<SplitExpenseModalFooter total={total} />}
      >
        <SplitExpenseForm vendor={vendor} total={total} expense={expense} />
      </Modal>
    </Formik>
  );
};
